import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const NEXT = {
  Scheduled: "Completed",
  Completed: "Raw Uploaded",
  "Raw Uploaded": null,
};

export default function Shoots() {
  const { effectiveRole } = useAuth();
  const [shoots, setShoots] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", shoot_title: "", shoot_date: "", notes: "" });

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/shoots"), api.get("/clients")]);
    setShoots(s.data);
    setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const submit = async () => {
    try {
      await api.post("/shoots", form);
      toast.success("Shoot scheduled");
      setOpen(false);
      setForm({ client_id: "", shoot_title: "", shoot_date: "", notes: "" });
      load();
    } catch { toast.error("Could not schedule shoot"); }
  };

  const advance = async (s) => {
    const next = NEXT[s.status];
    if (!next) return;
    const patch = { status: next };
    if (next === "Raw Uploaded") {
      patch.raw_footage_drive_url =
        prompt("Paste Drive URL for uploaded raw footage:", s.raw_footage_drive_url || "") ||
        s.raw_footage_drive_url;
    }
    await api.patch(`/shoots/${s.id}`, patch);
    toast.success(`Shoot marked ${next}`);
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <PageHeader
        eyebrow="05 / Production"
        title="Shoots"
        subtitle="Pipeline tracker from scheduled shoot to raw data upload. Once footage is uploaded, editors get the green light automatically."
        actions={
          effectiveRole === "admin" && (
            <Button
              data-testid="open-schedule-shoot-btn"
              onClick={() => setOpen(true)}
              className="rounded-full bg-white text-zinc-950 hover:bg-zinc-200"
            >
              <Plus className="h-4 w-4 mr-2" /> Schedule Shoot
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {["Scheduled", "Completed", "Raw Uploaded"].map((col) => (
          <div key={col} className="rounded-xl border border-white/5 bg-zinc-900/30 p-5">
            <div className="flex items-center justify-between mb-4">
              <StatusBadge value={col} />
              <span className="font-mono text-xs text-zinc-500">
                {shoots.filter((s) => s.status === col).length}
              </span>
            </div>
            <div data-testid={`shoot-col-${col.toLowerCase().replace(" ", "-")}`} className="space-y-3">
              {shoots
                .filter((s) => s.status === col)
                .map((s) => (
                  <div
                    key={s.id}
                    className="rounded-lg border border-white/10 bg-zinc-900 p-4 hover:border-white/20"
                    style={{ transition: "border-color 0.2s ease" }}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      {clientName(s.client_id)}
                    </div>
                    <div className="mt-1 font-medium leading-tight">{s.shoot_title}</div>
                    <div className="mt-3 text-xs text-zinc-500 font-mono">{s.shoot_date}</div>
                    {s.raw_footage_drive_url && (
                      <a
                        href={s.raw_footage_drive_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 block truncate text-xs text-emerald-400 hover:text-emerald-300 font-mono"
                      >
                        {s.raw_footage_drive_url}
                      </a>
                    )}
                    {effectiveRole === "admin" && NEXT[s.status] && (
                      <button
                        data-testid={`advance-shoot-${s.id}`}
                        onClick={() => advance(s)}
                        className="mt-4 inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-white"
                      >
                        Advance to {NEXT[s.status]} <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="schedule-shoot-dialog" className="bg-zinc-950 border-white/10 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Schedule a shoot</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="shoot-client-select" className="mt-1.5 bg-zinc-900 border-white/10">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shoot title</Label>
              <Input
                data-testid="shoot-title-input"
                value={form.shoot_title}
                onChange={(e) => setForm({ ...form, shoot_title: e.target.value })}
                className="mt-1.5 bg-zinc-900 border-white/10"
                placeholder="Q1 Campaign shoot"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                data-testid="shoot-date-input"
                type="date"
                value={form.shoot_date}
                onChange={(e) => setForm({ ...form, shoot_date: e.target.value })}
                className="mt-1.5 bg-zinc-900 border-white/10"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                data-testid="shoot-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 bg-zinc-900 border-white/10"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="submit-shoot-btn"
              disabled={!form.client_id || !form.shoot_title || !form.shoot_date}
              onClick={submit}
              className="bg-white text-zinc-950 hover:bg-zinc-200 rounded-full"
            >
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
